import type { ExerciseId } from "@/shared/domain/ids";

type JointRotation = Readonly<{ x?: number; y?: number; z?: number }>;

export type JointState = Readonly<{
  rootY?: number;
  spine?: JointRotation;
  lShoulder?: JointRotation;
  rShoulder?: JointRotation;
  lElbow?: JointRotation;
  rElbow?: JointRotation;
  lHip?: JointRotation;
  rHip?: JointRotation;
  lKnee?: JointRotation;
  rKnee?: JointRotation;
}>;

export type AnimateFn = (elapsedSeconds: number) => JointState;

const wave = (elapsedSeconds: number, frequency: number): number =>
  (Math.sin(elapsedSeconds * frequency) + 1) / 2;

const triangle = (elapsedSeconds: number, frequency: number): number =>
  Math.sin(elapsedSeconds * frequency);

const idle: AnimateFn = (elapsedSeconds) => ({
  spine: { x: Math.sin(elapsedSeconds * 1.2) * 0.015 },
});

export const ANIMS: Readonly<Record<string, AnimateFn>> = {
  default: idle,

  squat: (elapsedSeconds) => {
    const progress = wave(elapsedSeconds, 1.3);
    return {
      rootY: -progress * 0.65,
      spine: { x: progress * 0.32 },
      lHip: { x: progress * 1.3 },
      rHip: { x: progress * 1.3 },
      lKnee: { x: -progress * 1.65 },
      rKnee: { x: -progress * 1.65 },
      lShoulder: { x: 0.4 + progress * 0.6, z: -0.15 },
      rShoulder: { x: 0.4 + progress * 0.6, z: 0.15 },
      lElbow: { x: 0.2 },
      rElbow: { x: 0.2 },
    };
  },

  lunges: (elapsedSeconds) => {
    const signal = Math.sin(elapsedSeconds * 1.4);
    const progress = (signal + 1) / 2;
    const isForwardSide = signal >= 0;
    return {
      rootY: -progress * 0.35,
      lHip: { x: isForwardSide ? progress * 1.1 : -progress * 0.4 },
      rHip: { x: isForwardSide ? -progress * 0.4 : progress * 1.1 },
      lKnee: { x: isForwardSide ? -progress * 1.3 : -progress * 0.4 },
      rKnee: { x: isForwardSide ? -progress * 0.4 : -progress * 1.3 },
      spine: { x: progress * 0.1 },
    };
  },

  bicep_curl: (elapsedSeconds) => {
    const progress = wave(elapsedSeconds, 2.5);
    return {
      lElbow: { x: progress * 2.4 },
      rElbow: { x: progress * 2.4 },
      lShoulder: { x: 0, z: -0.08 },
      rShoulder: { x: 0, z: 0.08 },
    };
  },

  tricep_dip: (elapsedSeconds) => {
    const progress = wave(elapsedSeconds, 2.0);
    return {
      rootY: -progress * 0.4,
      lShoulder: { x: -0.3, z: -0.05 },
      rShoulder: { x: -0.3, z: 0.05 },
      lElbow: { x: progress * 1.7 },
      rElbow: { x: progress * 1.7 },
      spine: { x: 0.05 },
    };
  },

  overhead_press: (elapsedSeconds) => {
    const progress = wave(elapsedSeconds, 1.8);
    return {
      lShoulder: { x: 2.0 + progress * 1.0, z: -0.25 },
      rShoulder: { x: 2.0 + progress * 1.0, z: 0.25 },
      lElbow: { x: (1 - progress) * 1.6 + 0.3 },
      rElbow: { x: (1 - progress) * 1.6 + 0.3 },
    };
  },

  lateral_raise: (elapsedSeconds) => {
    const progress = wave(elapsedSeconds, 1.8);
    return {
      lShoulder: { x: 0, z: -progress * 1.5 },
      rShoulder: { x: 0, z: progress * 1.5 },
      lElbow: { x: 0.15 },
      rElbow: { x: 0.15 },
    };
  },

  bench_press: (elapsedSeconds) => {
    const progress = wave(elapsedSeconds, 1.8);
    return {
      lShoulder: { x: 1.55, z: -0.55 },
      rShoulder: { x: 1.55, z: 0.55 },
      lElbow: { x: 0.4 + progress * 1.4 },
      rElbow: { x: 0.4 + progress * 1.4 },
    };
  },

  pushup: (elapsedSeconds) => {
    const progress = wave(elapsedSeconds, 1.8);
    return {
      lShoulder: { x: 1.55, z: -0.55 },
      rShoulder: { x: 1.55, z: 0.55 },
      lElbow: { x: 0.3 + progress * 1.5 },
      rElbow: { x: 0.3 + progress * 1.5 },
    };
  },

  pullup: (elapsedSeconds) => {
    const progress = wave(elapsedSeconds, 1.5);
    return {
      lShoulder: { x: 2.7 - progress * 0.6, z: -0.3 },
      rShoulder: { x: 2.7 - progress * 0.6, z: 0.3 },
      lElbow: { x: 0.4 + progress * 1.8 },
      rElbow: { x: 0.4 + progress * 1.8 },
      rootY: progress * 0.4,
    };
  },

  row: (elapsedSeconds) => {
    const progress = wave(elapsedSeconds, 1.8);
    return {
      spine: { x: 0.7 },
      lShoulder: { x: 1.1 - progress * 0.5, z: -0.1 },
      rShoulder: { x: 1.1 - progress * 0.5, z: 0.1 },
      lElbow: { x: 0.4 + progress * 1.7 },
      rElbow: { x: 0.4 + progress * 1.7 },
      lHip: { x: 0.4 },
      rHip: { x: 0.4 },
      lKnee: { x: -0.3 },
      rKnee: { x: -0.3 },
    };
  },

  deadlift: (elapsedSeconds) => {
    const progress = wave(elapsedSeconds, 1.2);
    return {
      spine: { x: progress * 0.9 },
      lHip: { x: progress * 0.8 },
      rHip: { x: progress * 0.8 },
      lKnee: { x: -progress * 0.5 },
      rKnee: { x: -progress * 0.5 },
      rootY: -progress * 0.25,
      lShoulder: { x: 0.1 },
      rShoulder: { x: 0.1 },
    };
  },

  crunches: (elapsedSeconds) => {
    const progress = wave(elapsedSeconds, 1.8);
    return {
      spine: { x: progress * 0.7 },
      lShoulder: { x: 1.5 },
      rShoulder: { x: 1.5 },
      lElbow: { x: 1.6 },
      rElbow: { x: 1.6 },
    };
  },

  plank: () => ({
    spine: { x: 0.1 },
    lShoulder: { x: 1.6, z: -0.2 },
    rShoulder: { x: 1.6, z: 0.2 },
    lElbow: { x: 0.3 },
    rElbow: { x: 0.3 },
  }),

  russian_twist: (elapsedSeconds) => ({
    spine: { x: 0.5, y: triangle(elapsedSeconds, 2.4) * 0.8 },
    lShoulder: { x: 1.2 },
    rShoulder: { x: 1.2 },
    lElbow: { x: 1.0 },
    rElbow: { x: 1.0 },
    lHip: { x: 1.0 },
    rHip: { x: 1.0 },
    lKnee: { x: -1.2 },
    rKnee: { x: -1.2 },
  }),

  calf_raise: (elapsedSeconds) => {
    const progress = wave(elapsedSeconds, 2.5);
    return { rootY: progress * 0.18 };
  },

  hip_thrust: (elapsedSeconds) => {
    const progress = wave(elapsedSeconds, 1.6);
    return {
      spine: { x: -progress * 0.3 },
      lHip: { x: -progress * 0.4 },
      rHip: { x: -progress * 0.4 },
      lKnee: { x: -1.3 },
      rKnee: { x: -1.3 },
      rootY: -1.0 + progress * 0.4,
    };
  },

  leg_curl: (elapsedSeconds) => {
    const progress = wave(elapsedSeconds, 2.0);
    return {
      spine: { x: 1.3 },
      lHip: { x: 1.3 },
      rHip: { x: 1.3 },
      lKnee: { x: -progress * 1.7 - 0.2 },
      rKnee: { x: -progress * 1.7 - 0.2 },
      rootY: -1.2,
    };
  },

  shrugs: (elapsedSeconds) => {
    const progress = wave(elapsedSeconds, 2.5);
    return {
      lShoulder: { z: -0.05 - progress * 0.05 },
      rShoulder: { z: 0.05 + progress * 0.05 },
      rootY: progress * 0.06,
    };
  },
};

export const getAnimation = (exerciseId: ExerciseId | "default"): AnimateFn =>
  ANIMS[exerciseId] ?? ANIMS["default"] ?? idle;
